import {
  ArrowSquareOut,
  ArrowClockwise,
  DownloadSimple,
  FileText,
  ShieldCheck,
} from '@phosphor-icons/react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BusyLabel } from '@/components/busy-label'
import { KuniriLogo } from '@/components/kuniri-logo'
import { markSplashCompleted } from '@/app/splash/splash-state'
import { AuthLanguageSwitcher } from '../components/locale-switcher'
import { useSessionStore } from '@/lib/auth/session-store'

import { authApi } from '../api'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { logoutAccount } from '../session'
import { useGateStore } from '../state/gate-store'
import { useStartupStore } from './startup-store'
import { Notice } from '../components/notice'

/**
 * Global startup gates (D-043). Rendered instead of the route tree so an
 * outdated or unaccepted client cannot reach any screen; the legal documents
 * stay readable while the terms gate is active.
 */
export function StartupGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const location = useLocation()
  const status = useSessionStore((state) => state.status)
  const start = useStartupStore((state) => state.start)
  const retry = useStartupStore((state) => state.retry)
  const phase = useStartupStore((state) => state.phase)
  const error = useStartupStore((state) => state.error)
  const update = useGateStore((state) => state.update)
  const termsRequired = useGateStore((state) => state.termsRequired)

  useEffect(() => {
    if (status === 'unknown') void start()
  }, [status, start])

  const isLegalRoute =
    location.pathname === '/terms' || location.pathname === '/privacy'
  const gateShown =
    phase === 'error' ||
    Boolean(update?.required) ||
    (termsRequired && !isLegalRoute)

  useEffect(() => {
    // The splash has done its job once a blocking gate replaces the app.
    if (gateShown) markSplashCompleted()
  }, [gateShown])

  if (phase === 'error') {
    return (
      <main
        id="main-content"
        className="flex min-h-dvh flex-col items-center justify-center bg-paper px-6 py-10 text-center"
      >
        <div className="max-w-md">
          <h2 className="text-[25px] leading-none">
            {t('auth.startup.errorTitle')}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted">
            {t(friendlyErrorKey(error))}
          </p>
        </div>
        <button
          type="button"
          className="btn-primary mt-7 min-w-[180px]"
          onClick={() => void retry()}
        >
          <ArrowClockwise size={19} weight="bold" aria-hidden="true" />
          {t('common.retry')}
        </button>
      </main>
    )
  }

  if (update?.required) return <UpdateRequiredScreen onRetry={() => void retry()} />

  if (termsRequired && !isLegalRoute) {
    return <TermsGate />
  }

  return <>{children}</>
}

function GateChrome({
  children,
  align = 'center',
}: {
  children: ReactNode
  align?: 'center' | 'start'
}) {
  const mainRef = useRef<HTMLElement>(null)

  useEffect(() => {
    mainRef.current?.focus()
  }, [])

  return (
    <main
      id="main-content"
      ref={mainRef}
      tabIndex={-1}
      className="min-h-dvh bg-paper"
    >
      <div className="mx-auto flex min-h-dvh w-full max-w-[640px] flex-col px-6 pt-[calc(var(--safe-top)+16px)] pb-6 md:py-10">
        <header className="flex items-center justify-between gap-3">
          <KuniriLogo height={38} />
          <AuthLanguageSwitcher />
        </header>
        <div
          className={`flex flex-1 flex-col ${
            align === 'center' ? 'justify-center py-10' : 'pt-8'
          }`}
        >
          {children}
        </div>
      </div>
    </main>
  )
}

interface UpdateRequiredScreenProps {
  onRetry: () => void
}

/**
 * Update gate (D-043): platform-specific store URL and `forceUpdate` are
 * decided in `evaluateUpdate`. Web shows refresh guidance; native always has a
 * retry path, so the screen is never a dead end.
 */
export function UpdateRequiredScreen({ onRetry }: UpdateRequiredScreenProps) {
  const { t } = useTranslation()
  const update = useGateStore((state) => state.update)
  const [reloading, setReloading] = useState(false)
  if (!update) return null

  const isWeb = update.platform === 'web' || update.platform === 'api'
  const safeStoreUrl =
    update.storeUrl && /^https?:\/\//i.test(update.storeUrl)
      ? update.storeUrl
      : null
  const hasStoreUrl = safeStoreUrl !== null

  return (
    <GateChrome>
      <div className="mx-auto flex max-w-[520px] flex-col items-center text-center">
        <DownloadSimple
          size={60}
          weight="bold"
          aria-hidden="true"
          className="text-deep"
        />
        <h1 className="mt-5 text-[clamp(1.6rem,4vw,2rem)]">
          {t('auth.update.title')}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted">
          {isWeb
            ? t('auth.update.webBody')
            : hasStoreUrl
              ? t('auth.update.body')
              : t('auth.update.nativeNoStore')}
        </p>

        <div className="mt-7 flex w-full flex-col gap-2">
          {isWeb ? (
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => {
                setReloading(true)
                window.location.reload()
              }}
              aria-busy={reloading}
            >
              <BusyLabel
                busy={reloading}
                label={
                  <>
                    <ArrowClockwise size={20} weight="bold" aria-hidden="true" />
                    {t('auth.update.refresh')}
                  </>
                }
                busyLabel={t('auth.update.refreshing')}
              />
            </button>
          ) : null}

          {!isWeb && hasStoreUrl ? (
            <a
              href={safeStoreUrl ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="btn-primary w-full"
            >
              <ArrowSquareOut size={20} weight="bold" aria-hidden="true" />
              {t('auth.update.action')}
            </a>
          ) : null}

          <button type="button" className="btn-ghost w-full" onClick={onRetry}>
            {t('common.retry')}
          </button>
        </div>
      </div>
    </GateChrome>
  )
}

/**
 * Terms gate (UI_PARITY §3.5). Acceptance posts the current version to the
 * server gate; sign-out clears the session and returns to onboarding.
 */
export function TermsGate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const termsVersion = useGateStore((state) => state.currentTermsVersion)
  const setLegalStatus = useGateStore((state) => state.setLegalStatus)
  const markTermsAccepted = useGateStore((state) => state.markTermsAccepted)
  const resetStartup = useStartupStore((state) => state.reset)

  const [accepted, setAccepted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [signingOut, setSigningOut] = useState(false)

  async function accept() {
    if (!accepted || busy) return
    setBusy(true)
    setErrorKey(null)
    try {
      let version = termsVersion
      if (!version) {
        const legal = await authApi.legalStatus()
        version = legal.currentTermsVersion
        setLegalStatus(legal)
      }
      await authApi.acceptTerms(version)
      markTermsAccepted()
    } catch (error) {
      setErrorKey(friendlyErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  async function signOut() {
    setSigningOut(true)
    await logoutAccount()
    resetStartup()
    navigate('/onboarding', { replace: true })
  }

  return (
    <GateChrome align="start">
      <div className="mx-auto flex w-full max-w-[560px] flex-col">
        <h1 className="text-[22px] leading-none">{t('auth.termsGate.title')}</h1>
        <p className="mt-2 text-[15px] leading-relaxed text-muted">
          {t('auth.termsGate.body')}
        </p>

        <div className="mt-3 flex flex-col items-start">
          <Link
            to="/terms"
            className="btn-ghost min-h-11 justify-start px-0"
          >
            <FileText size={18} weight="bold" aria-hidden="true" />
            {t('auth.termsGate.readTerms')}
          </Link>
          <Link
            to="/privacy"
            className="btn-ghost min-h-11 justify-start px-0"
          >
            <ShieldCheck size={18} weight="bold" aria-hidden="true" />
            {t('auth.termsGate.readPrivacy')}
          </Link>
        </div>

        <label className="mt-3 flex items-start gap-3 text-sm font-semibold text-deep">
          <input
            type="checkbox"
            className="mt-0.5 size-5 shrink-0 accent-teal"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
            disabled={busy || signingOut}
          />
          <span>{t('auth.termsGate.accept')}</span>
        </label>

        {errorKey ? (
          <Notice tone="error" className="mt-3">
            {t(errorKey)}
          </Notice>
        ) : null}

        <button
          type="button"
          className="btn-primary mt-5 w-full"
          onClick={() => void accept()}
          disabled={!accepted || busy || signingOut}
          aria-busy={busy}
        >
          <BusyLabel
            busy={busy}
            label={t('auth.termsGate.continue')}
            busyLabel={t('auth.termsGate.accepting')}
          />
        </button>

        <button
          type="button"
          className="btn-ghost mt-1 w-full"
          onClick={() => void signOut()}
          disabled={busy || signingOut}
        >
          {t('auth.termsGate.signOut')}
        </button>
      </div>
    </GateChrome>
  )
}
